import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Plus, Trash2, Pencil, Check, X } from 'lucide-react';
import axios from '../../config/axios';
import {
  Container,
  PriceInputContainer,
  InputDiv,
  Label,
  Input,
  Select,
  PriceListContainer,
  PriceItemContainer,
  AddButton,
  DeleteButton
} from '../PriceManager/style';

export interface QuantityItem {
  quantity: number;
  unitOfMeasurementId: number;
}

export interface UnitOfMeasurement {
  id: number;
  name: string;
}

export interface QuantityManagerRef {
  addPendingQuantity: () => boolean;
  savePendingEdit: () => boolean;
}

interface QuantityManagerProps {
  quantities: QuantityItem[];
  onChange: (quantities: QuantityItem[]) => void;
  baseUrl: string;
  label?: string;
  materialSpecs?: {
    format?: string;
    density?: string | number;
    dimension1?: string | number;
    dimension2?: string | number;
  };
  setWarningMessage?: (msg: string) => void;
  isFashion?: boolean;
}

const QuantityManager = forwardRef<QuantityManagerRef, QuantityManagerProps>(({
  quantities,
  onChange,
  baseUrl,
  label = "Quantidades",
  materialSpecs,
  setWarningMessage,
  isFashion = false
}, ref) => {
  const [units, setUnits] = useState<UnitOfMeasurement[]>([]);
  const [newQuantity, setNewQuantity] = useState<{ quantity: string; unitOfMeasurementId: string }>({
    quantity: '',
    unitOfMeasurementId: ''
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');

  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const unitsEndpoint = `${baseUrl}/resource/util/units`;
        const response = await axios.get(unitsEndpoint);
        let unitsData = response.data || [];
        const allowedNames = ['kg', 'mm', 'un', 'unidade', 'm²', 'm linear', 'm2', 'm', 'linear', 'metro quadrado', 'metro linear'];
        unitsData = unitsData.filter((unit: any) => allowedNames.includes(unit.name?.toLowerCase()));
        setUnits(unitsData);
      } catch (error) {
        console.error('Erro ao buscar unidades de medida:', error);
      }
    };

    fetchUnits();
  }, [baseUrl]);

  const showAlert = (msg: string) => {
    if (setWarningMessage) {
      setWarningMessage(msg);
      setTimeout(() => setWarningMessage(''), 5000);
    } else {
      alert(msg);
    }
  };

  const calculateValue = (
    targetUnitName: string,
    otherQuantityValue: number
  ): number | null => {
    if (isFashion || !materialSpecs) return null;
    const { format, density, dimension1, dimension2 } = materialSpecs;

    const fmt = (format || '').toUpperCase().trim();
    const dens = Number(density);
    const d1 = Number(dimension1);
    const d2 = Number(dimension2) || 0;

    if (!fmt || !dens || !d1) {
      showAlert('Preencha formato, densidade e dimensão 1 na tela principal antes de calcular!');
      return null;
    }

    let area = 0;
    switch (fmt) {
      case 'BARRA':
      case 'REDONDO':
        area = Math.PI * (d1 / 2) ** 2;
        break;
      case 'QUADRADO':
      case 'CHAPA':
        if (!d2) {
          showAlert('Informe a dimensão 2 para o formato QUADRADO/CHAPA!');
          return null;
        }
        area = d1 * d2;
        break;
      case 'TUBO':
        if (!d2) {
          showAlert('Para TUBO informe as duas dimensões!');
          return null;
        }
        area = Math.PI * ((d1 / 2) ** 2 - (d2 / 2) ** 2);
        break;
      case 'LINGOTE':
        if (!d2) {
          showAlert('Informe a dimensão 2 para LINGOTE!');
          return null;
        }
        area = d1 * d2;
        break;
      default:
        showAlert('Formato de material inválido para cálculo!');
        return null;
    }

    if (targetUnitName === 'KG') {
      return (area * otherQuantityValue * dens) / 1_000_000;
    } else if (targetUnitName === 'MM') {
      return (otherQuantityValue * 1_000_000) / (area * dens);
    }

    return null;
  };

  const handleCalculateNewQuantity = () => {
    if (isFashion) return;
    const targetUnitId = Number(newQuantity.unitOfMeasurementId);
    const targetUnitName = getUnitName(targetUnitId).toUpperCase();

    const otherUnitName = targetUnitName === 'KG' ? 'MM' : 'KG';
    const otherUnit = units.find(u => u.name?.toUpperCase() === otherUnitName);
    const otherQtyItem = otherUnit ? quantities.find(q => q.unitOfMeasurementId === otherUnit.id) : null;

    if (!otherQtyItem) {
      showAlert(`Para calcular o ${targetUnitName === 'KG' ? 'Peso (KG)' : 'Comprimento (MM)'}, insira primeiro uma quantidade em ${otherUnitName} na lista!`);
      return;
    }

    const calculated = calculateValue(targetUnitName, otherQtyItem.quantity);
    if (calculated !== null) {
      setNewQuantity({
        ...newQuantity,
        quantity: String(calculated.toFixed(2)).replace('.', ',')
      });
      showAlert('');
    }
  };

  const handleCalculateEditingQuantity = (index: number, unitId: number) => {
    if (isFashion) return;
    const targetUnitName = getUnitName(unitId).toUpperCase();
    const otherUnitName = targetUnitName === 'KG' ? 'MM' : 'KG';
    const otherUnit = units.find(u => u.name?.toUpperCase() === otherUnitName);
    const otherQtyItem = otherUnit ? quantities.find(q => q.unitOfMeasurementId === otherUnit.id) : null;

    if (!otherQtyItem) {
      showAlert(`Para calcular o ${targetUnitName === 'KG' ? 'Peso (KG)' : 'Comprimento (MM)'}, insira primeiro uma quantidade em ${otherUnitName} na lista!`);
      return;
    }

    const calculated = calculateValue(targetUnitName, otherQtyItem.quantity);
    if (calculated !== null) {
      setEditingValue(String(calculated.toFixed(2)).replace('.', ','));
      showAlert('');
    }
  };

  const addQuantity = () => {
    if (newQuantity.quantity && newQuantity.unitOfMeasurementId) {
      const unitExists = quantities.some(q =>
        q.unitOfMeasurementId === parseInt(newQuantity.unitOfMeasurementId)
      );

      if (unitExists) {
        showAlert('Esta unidade de medida já foi adicionada. Escolha uma unidade diferente.');
        return false;
      }

      onChange([...quantities, {
        quantity: parseFloat(newQuantity.quantity.replace(',', '.')),
        unitOfMeasurementId: parseInt(newQuantity.unitOfMeasurementId)
      }]);
      setNewQuantity({ quantity: '', unitOfMeasurementId: '' });
      return true;
    }
    return false;
  };

  const addPendingQuantity = () => {
    if (newQuantity.quantity && newQuantity.unitOfMeasurementId) {
      const unitExists = quantities.some(q =>
        q.unitOfMeasurementId === parseInt(newQuantity.unitOfMeasurementId)
      );

      if (unitExists) {
        return false;
      }

      const newQtyItem = {
        quantity: parseFloat(newQuantity.quantity.replace(',', '.')),
        unitOfMeasurementId: parseInt(newQuantity.unitOfMeasurementId)
      };

      onChange([...quantities, newQtyItem]);
      setNewQuantity({ quantity: '', unitOfMeasurementId: '' });

      return true;
    }
    return false;
  };

  const savePendingEdit = () => {
    if (editingIndex !== null && editingValue) {
      const updatedQtyValue = parseFloat(editingValue.replace(',', '.')) || 0;
      const updatedQuantities = quantities.map((q, i) => i === editingIndex ? { ...q, quantity: updatedQtyValue } : q);
      onChange(updatedQuantities);
      setEditingIndex(null);
      setEditingValue('');
      return true;
    }
    return false;
  };

  useImperativeHandle(ref, () => ({
    addPendingQuantity,
    savePendingEdit
  }));

  const removeQuantity = (index: number) => {
    const updatedQuantities = quantities.filter((_, i) => i !== index);
    onChange(updatedQuantities);
  };

  const startEditing = (index: number, currentQty: number) => {
    setEditingIndex(index);
    setEditingValue(String(currentQty).replace('.', ','));
  };

  const saveEdit = (index: number) => {
    const updatedQtyValue = parseFloat(editingValue.replace(',', '.')) || 0;
    const updatedQuantities = quantities.map((q, i) => i === index ? { ...q, quantity: updatedQtyValue } : q);
    onChange(updatedQuantities);
    setEditingIndex(null);
    setEditingValue('');
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingValue('');
  };

  const availableUnits = units.filter(unit =>
    !quantities.some(q => q.unitOfMeasurementId === unit.id)
  );

  const getUnitName = (unitId: number) => {
    return units.find(unit => unit.id === unitId)?.name || '';
  };

  const showCalcNew = !isFashion && newQuantity.unitOfMeasurementId && 
    (getUnitName(Number(newQuantity.unitOfMeasurementId)).toUpperCase() === 'KG' || 
     getUnitName(Number(newQuantity.unitOfMeasurementId)).toUpperCase() === 'MM');

  return (
    <Container>
      <PriceInputContainer>
        <InputDiv style={{ position: 'relative' }}>
          <Label>Quantidade</Label>
          <Input
            type="text"
            value={newQuantity.quantity}
            onChange={(e) => setNewQuantity({ ...newQuantity, quantity: e.target.value })}
            placeholder="0,00"
            style={{ paddingRight: showCalcNew ? '35px' : '8px' }}
          />
          {showCalcNew && (
            <i
              className="fa fa-calculator"
              style={{
                position: 'absolute',
                right: '12px',
                top: '36px',
                cursor: 'pointer',
                color: '#007bff',
                fontSize: '14px'
              }}
              onClick={handleCalculateNewQuantity}
            />
          )}
        </InputDiv>

        <InputDiv>
          <Label>Unidade</Label>
          <Select
            value={newQuantity.unitOfMeasurementId}
            onChange={(e) => setNewQuantity({ ...newQuantity, unitOfMeasurementId: e.target.value })}
          >
            <option value=""></option>
            {availableUnits.map(unit => (
              <option key={unit.id} value={unit.id}>
                {unit.name.toUpperCase()}
              </option>
            ))}
          </Select>
        </InputDiv>
      </PriceInputContainer>

      <AddButton
        onClick={addQuantity}
        disabled={!newQuantity.quantity || !newQuantity.unitOfMeasurementId}
        style={{ marginTop: '0.5rem' }}
      >
        <Plus size={16} />
        {quantities.length === 0 ? `Adicionar ${label.toLowerCase()}` : 'Adicionar outra quantidade'}
      </AddButton>

      {quantities.length > 0 && (
        <PriceListContainer>
          {quantities.map((q, index) => {
            const showCalcEdit = !isFashion && (getUnitName(q.unitOfMeasurementId).toUpperCase() === 'KG' || 
                                 getUnitName(q.unitOfMeasurementId).toUpperCase() === 'MM');
            return (
              <PriceItemContainer key={index}>
                {editingIndex === index ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, position: 'relative' }}>
                    <Input
                      type="text"
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      style={{ flex: 1, padding: showCalcEdit ? '0 35px 0 8px' : '0 8px', height: '30px', borderRadius: '4px' }}
                    />
                    {showCalcEdit && (
                      <i
                        className="fa fa-calculator"
                        style={{
                          position: 'absolute',
                          right: '110px',
                          top: '8px',
                          cursor: 'pointer',
                          color: '#007bff',
                          fontSize: '13px'
                        }}
                        onClick={() => handleCalculateEditingQuantity(index, q.unitOfMeasurementId)}
                      />
                    )}
                    <span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>
                      {getUnitName(q.unitOfMeasurementId).toUpperCase()}
                    </span>
                    <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
                      <button 
                        onClick={() => saveEdit(index)} 
                        disabled={!editingValue}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#10b981', display: 'flex', alignItems: 'center' }}
                      >
                        <Check size={16} />
                      </button>
                      <button 
                        onClick={cancelEdit}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center' }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <span>
                      {q.quantity} {getUnitName(q.unitOfMeasurementId).toUpperCase()}
                    </span>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button 
                        onClick={() => startEditing(index, q.quantity)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#007bff', display: 'flex', alignItems: 'center', padding: 0 }}
                      >
                        <Pencil size={12} />
                      </button>
                      <DeleteButton onClick={() => removeQuantity(index)} style={{ margin: 0 }}>
                        <Trash2 size={12} />
                      </DeleteButton>
                    </div>
                  </>
                )}
              </PriceItemContainer>
            );
          })}
        </PriceListContainer>
      )}
    </Container>
  );
});

QuantityManager.displayName = 'QuantityManager';

export default QuantityManager;
